import { reverseTransformToDesiredFormat } from "../../helper/widget";

export const FILTER_SECTION_MAP = {
  metrics: "measure",
  dimension: "dimension",
  campaigns: "campaigns",
  topX: "topXConfig",
  comparisonPercentage: "measure",
};

export const COMPARISON_PERCENTAGE_CHIP_PREFIX = "% Change -";

export const EMPTY_FILTERS = {
  metrics: { custom_output: [], operator: "AND", data: [] },
  dimension: { custom_output: [], operator: "AND", data: [] },
  campaigns: { custom_output: [], operator: "AND", data: [] },
  topX: { custom_output: [], operator: "AND", data: [] },
  comparisonPercentage: { custom_output: [], operator: "AND", data: [] },
};

export const formatFilterUserInputValue = (userInput) => {
  if (Array.isArray(userInput)) {
    return userInput
      .filter((value) => value !== "" && value != null)
      .join(", ");
  }
  if (userInput === "" || userInput == null) return "";
  return `${userInput}`;
};

/** Hydrate custom_output-only filters into ComplexFilter module format (adds data). */
export const hydrateFilterModuleFilters = (filters = {}, config = {}) =>
  normalizeFiltersForChips(filters, config);

export const normalizeFiltersForChips = (filters = {}, config = {}) => {
  const nextFilters = JSON.parse(
    JSON.stringify({ ...EMPTY_FILTERS, ...filters })
  );

  Object.keys(FILTER_SECTION_MAP).forEach((sectionKey) => {
    const sectionConfigKey = FILTER_SECTION_MAP[sectionKey];
    const sectionConfig = config?.[sectionConfigKey];
    const existingData = nextFilters?.[sectionKey]?.data;
    const hasData = Array.isArray(existingData) && existingData.length > 0;
    const customOutput = nextFilters?.[sectionKey]?.custom_output;
    const hasCustomOutput =
      customOutput &&
      !Array.isArray(customOutput) &&
      Boolean(customOutput?.type);

    if (!hasData && hasCustomOutput && sectionConfig) {
      nextFilters[sectionKey].data = reverseTransformToDesiredFormat(
        customOutput,
        sectionConfig
      );
      nextFilters[sectionKey].operator =
        customOutput?.type === "OR_RULE" ? "OR" : "AND";
    } else if (!Array.isArray(nextFilters?.[sectionKey]?.data)) {
      nextFilters[sectionKey].data = [];
    }
    if (!nextFilters?.[sectionKey]?.operator) {
      nextFilters[sectionKey].operator = "AND";
    }
  });

  return nextFilters;
};

export const buildAppliedFilterChipData = (filters = {}, config = {}) => {
  const normalizedFilters = normalizeFiltersForChips(filters, config);
  const chips = [];

  Object.keys(FILTER_SECTION_MAP).forEach((sectionKey) => {
    const sectionConfigKey = FILTER_SECTION_MAP[sectionKey];
    const sectionFilters = normalizedFilters?.[sectionKey]?.data ?? [];
    const sectionFields = config?.[sectionConfigKey]?.fields ?? [];
    const sectionOperator =
      normalizedFilters?.[sectionKey]?.operator?.toLowerCase() === "or"
        ? "or"
        : "and";

    sectionFilters.forEach((group) => {
      const groupOperator =
        `${group?.operator || ""}`.toLowerCase() === "or" ? "or" : sectionOperator;

      (group?.data ?? []).forEach((condition) => {
        const fieldConfig = sectionFields.find(
          (field) =>
            field?.id === condition?.category ||
            field?.name === condition?.category
        );
        const fieldLabel =
          fieldConfig?.short_name ||
          fieldConfig?.display_name ||
          fieldConfig?.name ||
          condition?.category;
        const operatorLabel = condition?.filter ?? "";
        const valueLabel = formatFilterUserInputValue(condition?.userInput);

        const label = [fieldLabel, operatorLabel, valueLabel]
          .filter((item) => `${item}`.trim() !== "")
          .join(" ");

        if (label) {
          chips.push({
            key: `${sectionKey}_${group?.id}_${condition?.id}`,
            label,
            sectionLabel:
              sectionKey === "comparisonPercentage"
                ? COMPARISON_PERCENTAGE_CHIP_PREFIX
                : "",
            fieldLabel: fieldLabel || "",
            operatorLabel: operatorLabel || "",
            valueLabel: valueLabel || "",
            sectionKey,
            groupId: group?.id,
            conditionId: condition?.id,
            joiner: groupOperator,
          });
        }
      });
    });
  });

  return chips;
};

/**
 * Compact text preview using the same wording as AppliedFiltersChips labels.
 * Example: "ROAS Greater Than 0 and Spends Greater Than 0"
 */
export const buildAppliedFilterPreviewText = (filters = {}, config = {}) => {
  const chips = buildAppliedFilterChipData(filters, config);
  if (!chips.length) return "";

  const formatChipText = (chip) => {
    const rule = [chip.fieldLabel, chip.operatorLabel, chip.valueLabel]
      .filter((item) => `${item}`.trim() !== "")
      .join(" ");
    return chip.sectionLabel ? `${chip.sectionLabel} ${rule}`.trim() : rule;
  };

  let text = formatChipText(chips[0]);
  for (let i = 1; i < chips.length; i += 1) {
    const joiner = chips[i]?.joiner || "and";
    text += ` ${joiner} ${formatChipText(chips[i])}`;
  }
  return text;
};
